/* eslint-disable no-loop-func */
/* eslint-disable react/jsx-no-target-blank */
import {useEffect, useState} from 'react'
import {toAbsoluteUrl} from '../../../../helpers'
import {SidebarMenuItem} from './SidebarMenuItem'
import {useAuth} from '../../../../../app/modules/auth'
import {collection, doc, getDocs, onSnapshot, query, updateDoc, where} from 'firebase/firestore'
import db from '../../../../../services/firebase'

const OnSidebarMenuMain = () => {
  const [grandTotal, setGrandTotal] = useState(0)
  const [policyTotal, setPolicyTotal] = useState(0)
  const [completed, setCompleted] = useState(0)
  const {currentUser} = useAuth()
  const [requiredPolicies, setRequiredPolicies] = useState([]) 
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])

  useEffect(() => {
    let unsubscribeFrameworks: (() => void) | undefined;

    const setupFrameworkSubscription = async () => {
      try {
        const tenantId = currentUser?.tenantId;
        if (!tenantId) {
          setActiveFrameworks([]);
          return;
        }

        const tenantRef = doc(db, "tenants", tenantId);
        const frameworksCollectionRef = collection(tenantRef, "active_frameworks");

        unsubscribeFrameworks = onSnapshot(
          frameworksCollectionRef,
          (snapshot) => {
            const frameworksData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
              frameworks: doc.data().frameworks,
            }));

            const frameworks = frameworksData[0]?.frameworks || [];
            setActiveFrameworks(frameworks);
          },
          (error) => {
            console.error("Firestore subscription error:", error);
          }
        );
      } catch (error) {
        console.error("Setup subscription error:", error);
      }
    };

    setupFrameworkSubscription();

    return () => {
      if (unsubscribeFrameworks) {
        unsubscribeFrameworks();
      }
    };
  }, [currentUser?.tenantId]);

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'employees')
      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const employeesData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            fullName: doc.data().full_name,
            group: doc.data().group,
            uid: doc.data().uid,
          }))
          .filter((data) => data.uid === currentUser?.uid)

        if (employeesData.length > 0) {
          const group = employeesData[0].group
          const groupRef = collection(tenantRef, 'personnel_group')
          const unsubscribeGroup = onSnapshot(groupRef, (snapshot) => {
            const groupData = snapshot.docs
              .map((doc) => ({
                ...doc.data(),
                id: doc.id,
                checklist: doc.data().checklist,
                groupName: doc.data().groupName,
                uid: doc.data().uid,
              }))
              .filter((data) => data.groupName === group)

            if (groupData.length > 0) {
              const checklist = groupData[0].checklist
              const checklistRef = collection(tenantRef, 'personnel_checklist')
              const unsubscribeChecklist = onSnapshot(checklistRef, (snapshot) => {
                const checklistData = snapshot.docs
                  .map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                    checklist: doc.data().checklist_name,
                    policies: doc.data().policies,
                  }))
                  .filter((data) => data.checklist === checklist)
                
                if (checklistData.length > 0 && checklistData[0].policies) {
                  // Store policy names instead of IDs
                  setRequiredPolicies(checklistData[0].policies)
                } else {
                  setRequiredPolicies([])
                }
              })
            }
          })
        }
      })
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    const fetchPoliciesData = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const userId = currentUser?.uid || ''

        if (!tenantId || !userId) {
          return
        }

        const policiesRef = collection(db, 'tenants', tenantId, 'onboarding', userId, 'policies')
        const unsubscribe = onSnapshot(policiesRef, (snapshot) => {
          if (snapshot.empty || requiredPolicies.length === 0) {
            setPolicyTotal(0)
            return
          }

          // Get all checked policies from onboarding/policies
          const checkedPolicies = snapshot.docs
            .filter(doc => doc.data()?.isChecked)
            .map(doc => doc.data().policy) // Get policy names instead of IDs

          // Check if all required policies from personnel_checklist are checked
          const allRequiredPoliciesChecked = requiredPolicies.every(requiredPolicy => 
            checkedPolicies.includes(requiredPolicy)
          )

          // Set to 50 only if all current required policies are checked
          setPolicyTotal(allRequiredPoliciesChecked ? 50 : 0)
        })

        return () => {
          unsubscribe()
        }
      } catch (error) {
        console.error('Error fetching policies data:', error)
        setPolicyTotal(0)
      }
    }

    fetchPoliciesData()
  }, [currentUser, requiredPolicies])

  useEffect(() => {
    let unsubscribe = () => {}

    const fetchTrainingData = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const userId = currentUser?.uid || ''

        if (!tenantId || !userId) {
          return
        }

        let isSATChecked = false
        let isGDPRChecked = false

        const updateTotalCompleted = () => {
          if (activeFrameworks.includes('GDPR')) {
            setGrandTotal(isSATChecked && isGDPRChecked ? 50 : 0)
          } else {
            setGrandTotal(isSATChecked ? 50 : 0)
          }
        }
        const SATDocRef = doc(db, 'tenants', tenantId, 'onboarding', userId, 'training', 'SAT')
        const GDPRDocRef = doc(db, 'tenants', tenantId, 'onboarding', userId, 'training', 'GDPR')
        const unsubscribeSAT = onSnapshot(SATDocRef, (doc) => {
          const SATPCISecureData = doc.data() || {}
          isSATChecked = SATPCISecureData.is_checked || false
          updateTotalCompleted()
        })

        const unsubscribeGDPR = onSnapshot(GDPRDocRef, (doc) => {
          const GDPRData = doc.data() || {}
          isGDPRChecked = GDPRData.is_checked || false
          updateTotalCompleted()
        })

        unsubscribe = () => {
          unsubscribeSAT()
          unsubscribeGDPR()
        }
      } catch (error) {
        console.error('Error fetching training data:', error)
      }
    }

    fetchTrainingData()

    return () => {
      unsubscribe()
    }
  }, [currentUser,activeFrameworks])

  useEffect(() => {
    const totalCompleted = grandTotal + policyTotal
    setCompleted(totalCompleted)

    if (totalCompleted === 50 || totalCompleted === 100) {
      updateOnboardingStatus(totalCompleted)
    }
  }, [grandTotal, policyTotal])

  const updateOnboardingStatus = async (totalCompleted) => {
    try {
      const tenantId = currentUser?.tenantId
      const userUid = currentUser?.uid

      if (!tenantId || !userUid) {
        return
      }

      const employeesRef = collection(db, 'tenants', tenantId, 'employees')
      const q = query(employeesRef, where('uid', '==', userUid))
      const querySnapshot = await getDocs(q)

      if (querySnapshot.empty) {
        return
      }

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = String(currentDate.getMonth() + 1).padStart(2, '0')
      const day = String(currentDate.getDate()).padStart(2, '0')
      const todaysDate = year + '-' + month + '-' + day

      const employeeDoc = querySnapshot.docs[0]
      const employeeDocRef = doc(db, 'tenants', tenantId, 'employees', employeeDoc.id)
      
      if (totalCompleted === 100) {
        await updateDoc(employeeDocRef, {
          onboarding: 'Completed',
          onboardingEnded: todaysDate,
          securityTraining: grandTotal === 50 ? 'Complete' : 'Incomplete',
          acceptedPolicies: policyTotal === 50 ? 'Complete' : 'Incomplete',
        })
      } else {
        await updateDoc(employeeDocRef, {
          onboarding: 'Incomplete',
          securityTraining: grandTotal === 50 ? 'Complete' : 'Incomplete',
          acceptedPolicies: policyTotal === 50 ? 'Complete' : 'Incomplete',
        })
      }
    } catch (error) {
      console.error('Error updating onboarding status:', error)
    }
  }

  return (
    <>
      <div
        className='card-body d-flex align-items-center pt-2 justify-content-center'
        style={{marginTop: '-20px'}}
      >
        <div
          className='d-flex align-items-center flex-column mt-5 w-100 justify-content-start mb-5 p-5 pt-3 '
          style={{backgroundColor: '#19191D', borderRadius: '10px'}}
        >
          <div className='d-flex justify-content-between fw-bold fs-7 text-black opacity-75 w-100 mt-auto mb-2 pt-2'>
            <span className='text-gray-100'>Onboarding</span>
            <span className='text-muted fs-7'>
              {grandTotal === 50 && policyTotal === 50
                ? '2 of 2 complete'
                : (grandTotal === 50 ? 1 : 0) + (policyTotal === 50 ? 1 : 0) === 2
                ? '2 of 2 complete'
                : grandTotal === 50 || policyTotal === 50
                ? '1 of 2 complete'
                : '0 of 2 complete'}
            </span>
          </div>
          <div className='h-8px mx-0 w-100 rounded' style={{backgroundColor: '#DAF9EF'}}>
            <div
              className='rounded h-8px'
              role='progressbar'
              style={{
                width: `${Math.min(grandTotal + policyTotal, 100)}%`,
                backgroundColor: '#0E9F6E',
              }}
              aria-valuenow={Math.min(grandTotal + policyTotal, 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            ></div>
          </div>
          <div className='text-muted mt-2 fs-8'>
            {completed === 100 ? 'Onboarding has been completed' : ''}
          </div>
        </div>
      </div>

      <img src={toAbsoluteUrl(`/media/logos/line 4.svg`)} className='mb-6' alt='line' />
      <SidebarMenuItem to='/training' icon='copy-success' title='Training' fontIcon='bi-layers' />
      <SidebarMenuItem to='/policies' icon='document' title='Policies' fontIcon='bi-layers' />
    </>
  )
}

export {OnSidebarMenuMain}